import React, { useState, useRef, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { analyzeReportApi, getRecommendationsApi, getScannedReportsApi } from '../../../services/mainService';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
const Container = styled(Box)`
  padding: 20px;
  font-family: 'Poppins', sans-serif;
`;

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(9, 126, 183, 0.7); transform: scale(0.95); }
  70% { box-shadow: 0 0 0 20px rgba(9, 126, 183, 0); transform: scale(1); }
  100% { box-shadow: 0 0 0 0 rgba(9, 126, 183, 0); transform: scale(0.95); }
`;


const LoaderContainer = styled(Box)`
  text-align: center;
  padding: 40px;
  background: #f8fbff;
  border-radius: 20px;
  border: 1px solid #e3f2fd;
`;

const PulsingIcon = styled(Box)`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #097EB7 0%, #2AAB6C 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 25px auto;
  color: white;
  animation: ${pulse} 2s infinite;
  box-shadow: 0 10px 30px rgba(9, 126, 183, 0.3);
`;

const ProgressTrack = styled(Box)`
  width: 100%;
  max-width: 400px;
  height: 8px;
  background: #e9ecef;
  border-radius: 10px;
  margin: 20px auto;
  overflow: hidden;
`;

const ProgressBar = styled(Box)`
  height: 100%;
  background: linear-gradient(90deg, #097EB7 0%, #2AAB6C 100%);
  border-radius: 10px;
  transition: width 0.5s ease-in-out;
  width: ${props => props.width}%;
`;

const LoadingText = styled(Typography)`
  font-family: 'Poppins', sans-serif !important;
  font-weight: 600 !important;
  color: #097EB7;
  min-height: 24px; /* prevents jumping */
  transition: opacity 0.3s ease;
`;

const UploadSection = styled(Box)`
  background: #f8f9fa;
  border: 3px dashed #dee2e6;
  border-radius: 15px;
  padding: 60px 40px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 30px;

  &:hover {
    border-color: #097EB7;
    background: #f0f8ff;
    transform: translateY(-2px);
  }

  &.dragover {
    border-color: #097EB7;
    background: #e8f4ff;
    transform: scale(1.02);
  }
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 400px;
  border-radius: 15px;
  box-shadow: 0 8px 25px rgba(0,0,0,0.15);
  margin: 20px 0;
`;

const SummaryCard = styled(Card)`
  text-align: center;
  box-shadow: 0 4px 15px rgba(0,0,0,0.08) !important;
  border-radius: 15px !important;

  /* 🔴 FIX: remove enforced MUI min-height */
  min-height: unset !important;
`;

const PatientInfoBox = styled(Box)`
  background: linear-gradient(135deg, #097EB715 0%, #2AAB6C15 100%);
  padding: 25px;
  border-radius: 15px;
  margin-bottom: 30px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
`;

const JSONViewer = styled.pre`
  background: #282c34;
  color: #abb2bf;
  padding: 25px;
  border-radius: 15px;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  overflow-x: auto;
  max-height: 600px;
  line-height: 1.6;
`;

const RecommendationCard = styled(Card)`
  margin-bottom: 20px !important;
  border-left: 4px solid #097EB7 !important;
  box-shadow: 0 4px 15px rgba(0,0,0,0.08) !important;
  min-height: unset !important;
`;

const ReportListContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 8px; /* Tighter gap between rows */
`;

const ReportItemCard = styled(Box)`
  background-color: ${props => props.$selected ? '#e8f4ff' : '#fff'};
  display: flex;
  align-items: center;
  padding: 8px 12px; /* Very compact padding */
  border-radius: 8px;
  border: 1px solid ${props => props.$selected ? '#097EB7' : '#eef2f6'};
  box-shadow: ${props => props.$selected ? '0 2px 10px rgba(9, 126, 183, 0.18)' : 'none'};
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  height: auto;
  min-height: 0;

  &:hover {
    border-color: #097EB7;
    background-color: #f0f8ff;
    transform: translateX(4px); /* Slide slightly right on hover instead of up */
  }
`;

const FileIconBox = styled(Box)`
  width: 32px; /* Smaller Icon Box */
  height: 32px;
  border-radius: 6px;
  background: ${props => props.isPdf ? '#fff0f0' : '#e3f2fd'};
  color: ${props => props.isPdf ? '#d32f2f' : '#1976d2'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;
  font-size: 18px; /* Smaller icon size */
`;

const StatusChip = styled(Box)`
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${props => props.type === 'abnormal' ? '#ffebee' : '#f0fdf4'};
  color: ${props => props.type === 'abnormal' ? '#c62828' : '#166534'};
  border: 1px solid ${props => props.type === 'abnormal' ? '#fecaca' : '#dcfce7'};
`;
const ScannedReport = () => {
  const topRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);
  console.log(reportData, "Set Report");
  const [activeTab, setActiveTab] = useState(0);
  const [recommendations, setRecommendations] = useState(null);
  const [activeReportMeta, setActiveReportMeta] = useState(null);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [scannedReports, setScannedReports] = useState([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [listLoading, setListLoading] = useState(false);
  const [displayCount, setDisplayCount] = useState(5);
  const fileInputRef = useRef(null);
  const { userData } = useSelector(state => ({
    userData: state.auth.userData
  }))
    const [loadingStep, setLoadingStep] = useState(0);
  const loadingMessages = [
    "🚀 Uploading secure document...",
    "🔍 Scanning for medical data...",
    "🧬 Extracting lab values...",
    "🤖 AI identifying abnormalities...",
    "📊 Generating health insights...",
    "✅ Finalizing report..."
  ];

  // --- ADD THIS USE EFFECT ---
  // This cycles through messages and progress bar while 'loading' is true
  useEffect(() => {
    let interval;
    if (loading) {
      setLoadingStep(0); // Reset start
      interval = setInterval(() => {
        setLoadingStep((prev) => {
          if (prev < loadingMessages.length - 1) return prev + 1;
          return prev; // Stay on last message until API finishes
        });
      }, 2500); // Change text every 2.5 seconds
    }
    return () => clearInterval(interval);
  }, [loading]);

  // Calculate progress percentage based on step
  const progressPercent = Math.min(((loadingStep + 1) / loadingMessages.length) * 100, 95);

  const authToken = userData.token
  const ErrorModal = ({ isOpen, onClose, message }) => {
    if (!isOpen) return null;

    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
        }}
        onClick={onClose}
      >
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
            maxWidth: '400px',
            width: '90%',
            padding: '24px',
            position: 'relative',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              background: 'none',
              border: 'none',
              fontSize: '24px',
              color: '#999',
              cursor: 'pointer',
              padding: '4px 8px',
              lineHeight: 1,
            }}
          >
            ×
          </button>

          {/* Header with icon */}
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
            <div
              style={{
                width: '40px',
                height: '40px',
                backgroundColor: '#fee2e2',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <svg
                style={{ width: '24px', height: '24px', color: '#dc2626' }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h3
              style={{
                marginLeft: '12px',
                fontSize: '18px',
                fontWeight: 600,
                color: '#1f2937',
                margin: 0,
                marginLeft: '12px',
              }}
            >
              Error
            </h3>
          </div>

          {/* Message */}
          <p
            style={{
              color: '#6b7280',
              fontSize: '14px',
              lineHeight: '1.5',
              marginBottom: '20px',
              marginTop: '12px',
            }}
          >
            {message}
          </p>

          {/* Action button */}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={onClose}
              style={{
                padding: '8px 20px',
                backgroundColor: '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#b91c1c'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#dc2626'}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };


  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    processFile(file);
  };

  const processFile = (file) => {
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target.result);
      };
      reader.readAsDataURL(file);
      setError(null);
      setReportData(null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add('dragover');
  };

  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove('dragover');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('dragover');
    const file = e.dataTransfer.files[0];
    processFile(file);
  };

  const fetchScannedReports = async () => {
    try {
      setListLoading(true);
      const res = await getScannedReportsApi({
        token: authToken
      });

      setScannedReports(res.response.data || []);

    } catch (err) {
      console.error(err);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    fetchScannedReports();
  }, []);

  useEffect(() => {
    console.log('Scanned reports updated:', scannedReports);
  }, [scannedReports]);

  // Reset recommendations whenever the active report changes so the
  // Recommendations tab refreshes (re-fetches) for the newly selected/analyzed report
  // instead of showing the previous report's recommendations.
  useEffect(() => {
    setRecommendations(null);
  }, [reportData]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedFile) {
      setError('Please select a file');
      return;
    }

    setLoading(true);
    setError(null);
    setReportData(null);

    try {
      const res = await analyzeReportApi({
        token: authToken,
        file: selectedFile
      });
      if (res.response_type !== "fail") {
        setReportData(res.response.data);
        setSelectedReportId(res.response.data?._id || null);
        setActiveReportMeta({
          title: res.response.data?.reportType || 'Lab Report',
          fileName: selectedFile?.name,
          date: new Date().toISOString(),
        });
        setActiveTab(0);
        await fetchScannedReports();
        toast.success(res?.response?.message, { position: 'bottom-center', closeButton: false, autoClose: 1000, style: { width: "auto", borderRadius: 8 } });
      } else {
        setErrorMessage(res.response?.message || 'Failed to analyze report');
        setShowErrorModal(true);
      }


    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getRecommendations = async () => {
    if (!reportData) return;

    setLoadingRecommendations(true);

    try {
      const res = await getRecommendationsApi({
        token: authToken,
        data: reportData
      });

      if (res.response_type !== "fail") {
        setRecommendations(res.response.data);
        toast.success(res?.response?.message, { position: 'bottom-center', closeButton: false, autoClose: 1000, style: { width: "auto", borderRadius: 8 } });
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoadingRecommendations(false);
    }
  };

  const getStatusColor = (status) => {
    if (!status) return 'default';
    const s = status.toLowerCase();
    if (s.includes('normal')) return 'success';
    if (s.includes('high') || s.includes('abnormal')) return 'error';
    if (s.includes('low')) return 'warning';
    return 'default';
  };

  const copyJSON = () => {
    if (reportData) {
      navigator.clipboard.writeText(JSON.stringify(reportData, null, 2));
      alert('✅ JSON copied to clipboard!');
    }
  };

  const downloadJSON = () => {
    if (reportData) {
      const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `blood-report-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const normalizeReportData = (raw) => {
    if (!raw) return null;

    if (raw.tests && raw.summary) {
      return raw;
    }

    if (raw.structuredData) {
      const mainRawData = raw.structuredData
      return { ...mainRawData, summary: raw.summary };
    }

    return null;
  };

  return (
    <>
      <Container ref={topRef}>
        <Typography variant="h5" gutterBottom sx={{ fontFamily: 'Poppins', fontWeight: 600, color: '#2B2B2B', mb: 3 }}>
          🩺 AI Blood Report Analyzer
        </Typography>

        <form onSubmit={handleSubmit}>
          <UploadSection
            onClick={() => fileInputRef.current?.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <CloudUploadIcon sx={{ fontSize: 80, color: '#097EB7', mb: 2 }} />
            <Typography variant="h6" sx={{ fontFamily: 'Poppins', mb: 1 }}>
              AI-Powered Report Analysis
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Secure AI extracts test values, detects abnormalities, and summarizes results
            </Typography>

            <Typography variant="caption" color="text.secondary">
              Accepted formats: JPG, PNG, WEBP, PDF • Maximum 10MB
            </Typography>

          </UploadSection>

          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept="image/*,application/pdf"
            style={{ display: 'none' }}
          />

          {preview && (
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              {selectedFile && selectedFile.type === 'application/pdf' ? (
                <Box
                  sx={{
                    border: '1px solid #ddd',
                    borderRadius: '15px',
                    overflow: 'hidden',
                    height: '500px',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                    margin: '20px 0'
                  }}
                >
                  <iframe
                    src={preview}
                    title="PDF Preview"
                    width="100%"
                    height="100%"
                    style={{ border: 'none' }}
                  />
                </Box>
              ) : (
                <PreviewImage src={preview} alt="Preview" />
              )}
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {selectedFile && (
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  background: 'linear-gradient(90deg, #097EB7 0%, #2AAB6C 100%)',
                  borderRadius: '50px',
                  padding: '12px 40px',
                  fontFamily: 'Poppins',
                  fontWeight: 600,
                  textTransform: 'none'
                }}
              >
                {loading ? <CircularProgress size={24} /> : '🔍 Analyze Report with AI'}
              </Button>
            </Box>
          )}
        </form>

        {loading && (
  <LoaderContainer>
    <PulsingIcon>
      {/* You can change this icon to a Brain or Scanner icon if you prefer */}
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
      </svg>
    </PulsingIcon>
    
    <LoadingText variant="h6">
      {loadingMessages[loadingStep]}
    </LoadingText>
    
    <ProgressTrack>
      <ProgressBar width={progressPercent} />
    </ProgressTrack>
    
    <Typography variant="caption" sx={{ color: '#888', mt: 1, display: 'block' }}>
      AI analysis takes 10-20 seconds. Please do not close the window.
    </Typography>
  </LoaderContainer>
)}

        {reportData && (
          <Box sx={{ mt: 4 }}>
            <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tab label="📊 Overview" sx={{ fontFamily: 'Poppins' }} />
              <Tab label="🔬 Test Details" sx={{ fontFamily: 'Poppins' }} />
              <Tab label="💡 Recommendations" sx={{ fontFamily: 'Poppins' }} />
            </Tabs>

            {/* Overview Tab */}
            {activeTab === 0 && (
              <Box>
                {reportData.patientInfo && (
                  <PatientInfoBox>
                    {Object.entries(reportData.patientInfo).map(([key, value]) => (
                      value && (
                        <Box key={key}>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', textTransform: 'uppercase' }}>
                            {key}
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: '#333' }}>
                            {value}
                          </Typography>
                        </Box>
                      )
                    ))}
                  </PatientInfoBox>
                )}

                {reportData.summary && (
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 4 }}>
                    <SummaryCard>
                      <CardContent>
                        <Typography variant="h3" sx={{ color: '#097EB7', fontWeight: 700 }}>
                          {reportData.summary.totalTests || 0}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#666', textTransform: 'uppercase' }}>
                          Total Tests
                        </Typography>
                      </CardContent>
                    </SummaryCard>

                    <SummaryCard>
                      <CardContent>
                        <Typography variant="h3" sx={{ color: '#28a745', fontWeight: 700 }}>
                          {reportData.summary.normalCount || 0}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#666', textTransform: 'uppercase' }}>
                          Normal
                        </Typography>
                      </CardContent>
                    </SummaryCard>

                    <SummaryCard>
                      <CardContent>
                        <Typography variant="h3" sx={{ color: '#dc3545', fontWeight: 700 }}>
                          {reportData.summary.abnormalCount ?? reportData.summary.abnormal ?? 0}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#666', textTransform: 'uppercase' }}>
                          Abnormal
                        </Typography>
                      </CardContent>
                    </SummaryCard>
                  </Box>
                )}

                {reportData.tests && reportData.tests.length > 0 && (
                  <TableContainer component={Card}>
                    <Table>
                      <TableHead sx={{ background: 'linear-gradient(90deg, #097EB7 0%, #2AAB6C 100%)' }}>
                        <TableRow>
                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Parameter</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Result</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Unit</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Reference Range</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reportData.tests.map((test, index) => (
                          <TableRow key={index} hover>
                            <TableCell sx={{ fontWeight: 600 }}>{test.parameter}</TableCell>
                            <TableCell>{test.result}</TableCell>
                            <TableCell>{test.unit || '-'}</TableCell>
                            <TableCell>{test.referenceRange || '-'}</TableCell>
                            <TableCell>
                              <Chip
                                label={test.status || 'Unknown'}
                                color={getStatusColor(test.status)}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}

            {/* Test Details Tab */}
            {activeTab === 1 && reportData.tests && reportData.tests.length > 0 && (
              <Box>
                {activeReportMeta && (
                  <Box
                    sx={{
                      mb: 3,
                      p: 2,
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #097EB715 0%, #2AAB6C15 100%)',
                      borderLeft: '4px solid #097EB7',
                    }}
                  >
                    <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Test details for
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2B2B2B' }}>
                      {activeReportMeta.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      {[activeReportMeta.fileName, activeReportMeta.date && new Date(activeReportMeta.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })]
                        .filter(Boolean)
                        .join('  •  ')}
                    </Typography>
                  </Box>
                )}
                <TableContainer component={Card}>
                <Table>
                  <TableHead sx={{ background: 'linear-gradient(90deg, #097EB7 0%, #2AAB6C 100%)' }}>
                    <TableRow>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Parameter</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Result</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Unit</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Reference Range</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reportData.tests.map((test, index) => (
                      <TableRow key={index} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{test.parameter}</TableCell>
                        <TableCell>{test.result}</TableCell>
                        <TableCell>{test.unit || '-'}</TableCell>
                        <TableCell>{test.referenceRange || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={test.status || 'Unknown'}
                            color={getStatusColor(test.status)}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </TableContainer>
              </Box>
            )}

            {/* Recommendations Tab */}
            {activeTab === 2 && (
              <Box>
                {!recommendations ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={getRecommendations}
                      disabled={loadingRecommendations}
                      sx={{
                        background: 'linear-gradient(90deg, #097EB7 0%, #2AAB6C 100%)',
                        borderRadius: '50px',
                        padding: '12px 40px',
                        fontFamily: 'Poppins',
                        fontWeight: 600,
                        textTransform: 'none'
                      }}
                    >
                      {loadingRecommendations ? <CircularProgress size={24} /> : 'Get AI Health Recommendations'}
                    </Button>
                  </Box>
                ) : (
                  <Box>
                    {activeReportMeta && (
                      <Box
                        sx={{
                          mb: 3,
                          p: 2,
                          borderRadius: '12px',
                          background: 'linear-gradient(135deg, #097EB715 0%, #2AAB6C15 100%)',
                          borderLeft: '4px solid #097EB7',
                        }}
                      >
                        <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Recommendations for
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2B2B2B' }}>
                          {activeReportMeta.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666' }}>
                          {[activeReportMeta.fileName, activeReportMeta.date && new Date(activeReportMeta.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })]
                            .filter(Boolean)
                            .join('  •  ')}
                        </Typography>
                      </Box>
                    )}
                    {recommendations.recommendations && recommendations.recommendations.length > 0 && (
                      <Box>
                        {recommendations.recommendations.map((rec, index) => (
                          <RecommendationCard key={index}>
                            <CardContent>
                              <Typography variant="h6" sx={{ fontWeight: 700, color: '#097EB7', mb: 1 }}>
                                📌 {rec.category}
                              </Typography>
                              <Typography variant="body1" sx={{ mb: 1 }}>
                                {rec.suggestion}
                              </Typography>
                              <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#666' }}>
                                💡 {rec.reason}
                              </Typography>
                            </CardContent>
                          </RecommendationCard>
                        ))}
                      </Box>
                    )}

                    {recommendations.disclaimer && (
                      <Alert style={{ minHeight: "0" }} severity="warning" sx={{ mt: 3 }}>
                        <strong>⚠️ Disclaimer:</strong> {recommendations.disclaimer}
                      </Alert>
                    )}
                  </Box>
                )}
              </Box>
            )}

            {/* JSON Data Tab */}
            {activeTab === 3 && (
              <Box>
                <Box sx={{ mb: 2 }}>
                  <Button variant="outlined" onClick={copyJSON} sx={{ mr: 1, fontFamily: 'Poppins' }}>
                    📋 Copy JSON
                  </Button>
                  <Button variant="outlined" onClick={downloadJSON} sx={{ fontFamily: 'Poppins' }}>
                    💾 Download JSON
                  </Button>
                </Box>
                <JSONViewer>{JSON.stringify(reportData, null, 2)}</JSONViewer>
              </Box>
            )}
          </Box>
        )}


      </Container>

      <Box sx={{ mt: 6 }}>
        <Typography
          variant="h6"
          sx={{ fontFamily: 'Poppins', fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center' }}
        >
          📁 Medical History & Past Reports
          <Chip label={scannedReports.length} size="small" sx={{ ml: 2, bgcolor: '#e3f2fd', color: '#097EB7', fontWeight: 700 }} />
        </Typography>

        {listLoading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={30} sx={{ color: '#097EB7' }} />
            <Typography variant="body2" sx={{ mt: 2, color: '#666' }}>Loading history...</Typography>
          </Box>
        ) : scannedReports.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6, bgcolor: '#f8f9fa', borderRadius: '16px', border: '2px dashed #e9ecef' }}>
            <InsertDriveFileIcon sx={{ fontSize: 48, color: '#adb5bd', mb: 2 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#495057' }}>No Reports Found</Typography>
            <Typography variant="body2" color="text.secondary">Upload your first report above to get started.</Typography>
          </Box>
        ) : (
          <ReportListContainer>
            {scannedReports
              .slice(0, displayCount)
              .map((report) => {
                const isPdf = report.source?.fileType?.includes('pdf') || report.source?.fileName?.endsWith('.pdf');
                const abnormalCount = report.summary?.abnormalCount || 0;
                const hasIssues = abnormalCount > 0;
                const reportDate = new Date(report.createdAt);

                return (
                  <ReportItemCard
                    key={report._id}
                    $selected={selectedReportId === report._id}
                    onClick={() => {
                      const normalized = normalizeReportData(report.analysis ?? report);
                      if (!normalized) {
                        alert('Analysis data not available');
                        return;
                      }
                      setReportData(normalized);
                      setSelectedReportId(report._id);
                      setActiveReportMeta({
                        title: report.reportType || 'Lab Report',
                        fileName: report.source?.fileName,
                        date: report.createdAt,
                      });
                      setActiveTab(0);

                      if (topRef.current) {
                        topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                  >
                    {/* 1. Slim Icon */}
                    <FileIconBox isPdf={isPdf}>
                      {isPdf ? <PictureAsPdfOutlinedIcon fontSize="inherit" /> : <ImageOutlinedIcon fontSize="inherit" />}
                    </FileIconBox>

                    {/* 2. Compact Text Info */}
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}> {/* minWidth: 0 helps text truncate properly */}
                      <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#333', mr: 1, fontSize: '0.85rem' }}>
                          {report.reportType || "Lab Report"}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#888', fontSize: '0.7rem' }}>
                          {reportDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </Typography>
                      </Box>

                      <Typography variant="caption" sx={{ color: '#666', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.75rem' }}>
                        {report.source?.fileName}
                      </Typography>
                    </Box>

                    {/* 3. Slim Badge */}
                    <Box sx={{ ml: 2, display: { xs: 'none', sm: 'block' } }}>
                      {hasIssues ? (
                        <StatusChip type="abnormal">
                          ⚠️ {abnormalCount} Issues
                        </StatusChip>
                      ) : (
                        <StatusChip type="normal">
                          Normal
                        </StatusChip>
                      )}
                    </Box>

                    {/* 4. Small Arrow */}
                    <Box sx={{ ml: 1, color: '#ccc', display: 'flex' }}>
                      <KeyboardArrowRightIcon fontSize="small" />
                    </Box>
                  </ReportItemCard>
                );
              })}
          </ReportListContainer>
        )}

        {/* Show More Button */}
        {displayCount < scannedReports.length && (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button
              onClick={() => setDisplayCount(displayCount + 5)}
              sx={{ color: '#666', textTransform: 'none', '&:hover': { color: '#097EB7', bgcolor: 'transparent' } }}
            >
              View Older Reports ({scannedReports.length - displayCount} more)
            </Button>
          </Box>
        )}
      </Box>

      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => {
          setShowErrorModal(false);
          setErrorMessage('');
        }}
        message={errorMessage}
      />
    </>
  );
};

export default ScannedReport;